/**
 * The control a nonprofit demo teacher uses to see the product as their linked
 * sample student, and to come back.
 *
 * Renders nothing at all unless the server said this account may switch, so it
 * is safe to mount unconditionally on the phone's account surfaces. From `sm`
 * up the switch is a row in the app bar's account panel (`AccountMenu`), which
 * reads the same state through `useDemoViewSwitch`. While the sample-student
 * view is on, the account chrome around it already describes the student —
 * this control's job is to say plainly that the view is borrowed and to offer
 * the way out. On phones, where the app bar is hidden, the `bar` layout keeps
 * that way out on every page as a slim strip on top of the bottom bar. The
 * starting-point quiz (`/diagnostic`) has no app shell at all, so it pins the
 * same strip to the foot of the viewport on every width.
 *
 * The status chip uses the amber-wash treatment (DESIGN.md §4/§15): amber is
 * the surface, the words stay ink, because "you are not looking at your own
 * account" is a state nobody may misread.
 *
 * Update when the switch endpoints, their copy, or the surfaces that host this
 * control change.
 */

import { type FC, useCallback, useState } from 'react';
import { Alert, alpha, Box, Button, Chip, Typography } from '@mui/material';

import {
  describeDemoViewError,
  enterDemoStudentView,
  exitDemoStudentView,
} from '@/auth/demoView';
import { DEMO_VIEW_BAR_HEIGHT } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';

/** The status every surface shows while the view is on. */
export const SAMPLE_VIEW_LABEL = 'Sample student view';

type SwitchAction = (getAccessToken: () => Promise<string>) => Promise<void>;

/**
 * The switch's state and its one action, for every surface that hosts it.
 * `label` is the action's wording, and reads "Switching…" from the click
 * until the page reloads into the other view.
 */
export function useDemoViewSwitch() {
  const { demoView, getAccessToken } = useAuth();
  const [isSwitching, setIsSwitching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runSwitch = useCallback(
    async (action: SwitchAction) => {
      setErrorMessage(null);
      setIsSwitching(true);
      try {
        await action(getAccessToken);
        // A successful switch navigates out of this document, so the control
        // deliberately stays busy until it does — a second click would fire a
        // second switch against a page that is already leaving.
      } catch (err) {
        setErrorMessage(describeDemoViewError(err));
        setIsSwitching(false);
      }
    },
    [getAccessToken]
  );
  const clearError = useCallback(() => setErrorMessage(null), []);

  const available = Boolean(demoView?.available);
  const active = available && Boolean(demoView?.active);
  return {
    available,
    active,
    isSwitching,
    errorMessage,
    clearError,
    label: isSwitching
      ? 'Switching…'
      : active
        ? 'Back to teacher view'
        : 'View as sample student',
    toggle: () => {
      void runSwitch(active ? exitDemoStudentView : enterDemoStudentView);
    },
  };
}

interface DemoViewSwitchProps {
  /**
   * `drawer` stacks and centres the chip, button and any error under the
   * phone drawer's account block, and owns the gap to whatever it sits below
   * so a hidden control leaves no hole. `bar` is the one-line strip, shown
   * only while the view is on: on top of the phone bottom bar, or pinned on
   * its own where there is no app shell. Its host reserves exactly its
   * height, so it never grows: its error floats above it in full instead.
   */
  layout?: 'drawer' | 'bar';
  size?: 'small' | 'medium';
}

const DemoViewSwitch: FC<DemoViewSwitchProps> = ({
  layout = 'drawer',
  size = 'medium',
}) => {
  const {
    available,
    active,
    isSwitching,
    errorMessage,
    clearError,
    label,
    toggle,
  } = useDemoViewSwitch();

  if (!available) return null;

  if (layout === 'bar') {
    if (!active) return null;
    return (
      <Box
        sx={theme => ({
          position: 'relative',
          height: DEMO_VIEW_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          px: 1.5,
          bgcolor: alpha(theme.palette.warning.dark, 0.16),
          borderBottom: `1px solid ${alpha(theme.palette.warning.dark, 0.55)}`,
        })}
      >
        {errorMessage && (
          // Inside the fixed bar, so it stays on screen; above the strip, so
          // the whole sentence fits a 320px phone. Dismissable, because it
          // covers the page until the next tap.
          <Alert
            severity="error"
            onClose={clearError}
            sx={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 'calc(100% + 8px)',
              boxShadow: 3,
              '& .MuiAlert-action': { pt: 0, alignItems: 'center' },
              '& .MuiAlert-action .MuiIconButton-root': {
                width: 48,
                height: 48,
              },
            }}
          >
            {errorMessage}
          </Alert>
        )}
        <Typography
          role="status"
          variant="caption"
          noWrap
          sx={{ fontWeight: 800, color: 'text.primary', minWidth: 0 }}
        >
          {SAMPLE_VIEW_LABEL}
        </Typography>
        <Typography
          aria-hidden
          variant="caption"
          sx={{ fontWeight: 800, color: 'text.primary' }}
        >
          ·
        </Typography>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={isSwitching}
          onClick={toggle}
          sx={{
            minHeight: DEMO_VIEW_BAR_HEIGHT,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            fontWeight: 800,
          }}
        >
          {label}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        mt: 1.25,
      }}
    >
      {active && (
        <Chip
          role="status"
          size="small"
          label={SAMPLE_VIEW_LABEL}
          sx={theme => ({
            height: 22,
            borderRadius: theme.custom.radii.pill,
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.66rem',
            color: 'text.primary',
            bgcolor: alpha(theme.palette.warning.dark, 0.16),
            border: `1px solid ${alpha(theme.palette.warning.dark, 0.55)}`,
            '& .MuiChip-label': { px: 1 },
          })}
        />
      )}
      <Button
        variant="outlined"
        color="primary"
        size={size}
        disabled={isSwitching}
        onClick={toggle}
        sx={theme => ({
          borderRadius: theme.custom.radii.pill,
          whiteSpace: 'nowrap',
        })}
      >
        {label}
      </Button>
      {errorMessage && (
        <Typography
          role="alert"
          variant="caption"
          sx={{ color: 'error.main', textAlign: 'center' }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default DemoViewSwitch;
